// Wiring del modale builder: collega HTML statico, stato puro, prompt
// e blueprint. Pattern dei modali dell’app: build → wire → Promise.

import $ from 'jquery';
import {
  CUSTOM_ELEMENT_KEY,
  SACRED_SCENE_KEY,
  GRADIENT_TO_TONES,
  findElementItem,
  findZoneCombo,
  elementFigureLevers,
  isSoftFrameStyle,
} from './builderCatalog';
import {
  createBuilderState,
  setEssence,
  setDirection,
  setScene,
  setTechnique,
  setIntensity,
  setPalette,
  setAtmosphere,
  setTextReadability,
  setSpread,
  setGradientIntensity,
  setFromColor,
  setToColor,
  resolvedGradientEndpoints,
  setMirror,
  setScenePlacement,
  setSceneExtent,
  setSceneFade,
  setSacredFigure,
  setSacredFraming,
  setSacredPerspective,
  setSacredMood,
  setCustomScene,
  setCustomSacredFigure,
  setCustomSacredFraming,
  setCustomSacredPerspective,
  setCustomSacredMood,
  setCustomTechnique,
  setCustomIntensity,
  setCustomAtmosphere,
  setCustomPalette,
  setPaper,
  setPaperOn,
  setGrain,
  setMaterial,
  setMaterialIntensity,
  startCellSelection,
  toggleSelectionCell,
  clearSelection,
  confirmSelectionElement,
  setElementItem,
  setElementOption,
  toggleElementCell,
  removeElementByCell,
  getElementByCell,
  canAddElement,
  hasImagery,
  supportsAtmosphere,
  setFrameStyle,
  setFrameRichness,
  setFrameCoverage,
  setFrameRelation,
  setFrameCustomStyle,
  setFrameCustomCoverage,
  setFrameMargins,
  setFrameMargin,
  setDetails,
} from './builderState';
import { composeBuilderPrompt } from './structuredPrompt';
import { composeSetBrief } from './setBriefExport';
import { generateBuilderPreview } from './builderPreview';
import { openBuilderColorOverlay } from './builderColorOverlay';
import {
  cellClickAction,
  snapExtentKey,
  bandFractionFromPointer,
  gradientFractionFromPointer,
  snapSpreadKey,
} from './blueprintInteractions';
import { renderBlueprintHtml } from './blueprintPreview';
import {
  MODAL_ID,
  TABS,
  TAB_TITLES,
  BUILDER_MODAL_STYLES,
  buildBuilderModalHtml,
} from './builderModalHtml';
import { elementIconSvg } from './builderIcons';
import { isLoggedUserSuperUser } from '~src/user';

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export function wireBuilderModal(
  $root,
  {
    isSuperUser = false,
    orientation = 'vertical',
    ratio,
    onConfirm,
    generatePreview = async ({ prompt, orientation: verso }) => {
      // Import a runtime, non in cima al file: `generateAiBackground` tira
      // dentro il client HTTP e con lui axios in ESM, che jest non sa leggere.
      // Cosi' il peso lo paga solo il super user che clicca, e i test del modal
      // restano montabili senza toccare la rete.
      const { default: generateAiBackground } = await import(
        '../generateAiBackground'
      );
      return generateBuilderPreview({
        prompt,
        orientation: verso,
        generate: generateAiBackground,
      });
    },
    // Il preload evita di montare un'immagine ancora in download: il crossfade
    // parte solo quando il browser la possiede. onerror risolve comunque: se il
    // download fallisce ci pensa l'<img> nel DOM col suo alt, senza bloccare
    // previewInFlight per sempre.
    preloadPreviewImage = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = url;
      }),
    // Import statico: il modulo dell'overlay non pesa, il picker che monta al
    // suo interno resta a import dinamico (vedi builderColorOverlay.js).
    openColorOverlay = openBuilderColorOverlay,
  } = {}
) {
  let state = createBuilderState({ orientation });
  let activeTab = 'base';
  let editCell = null;
  let promptDirty = false;
  let setBriefDirty = false;
  const $prompt = isSuperUser ? $root.find('.ai-bgb-prompt') : null;
  const $setBrief = isSuperUser ? $root.find('.ai-bgb-setbrief') : null;
  const $preview = isSuperUser ? $root.find('.ai-bgb-preview') : null;
  let previewInFlight = false;
  let previewPending = Promise.resolve();
  // Prompt dell'ultima anteprima riuscita, catturato al click: e' il metro con
  // cui updatePreviewState decide se l'immagine e' ancora attuale.
  let lastPreviewPrompt = null;

  const currentPrompt = () => {
    if (isSuperUser && promptDirty && $prompt && $prompt.length) {
      return $prompt.val();
    }
    return composeBuilderPrompt(state);
  };

  // Il brief esporta il prompt corrente, riscritture a mano comprese: sta in una
  // funzione a parte perche' va rifatto a ogni battuta nella textarea del
  // prompt, dove un render completo ruberebbe il fuoco.
  const renderSetBrief = ({ force = false } = {}) => {
    if (!isSuperUser || !$setBrief || !$setBrief.length) {
      return;
    }
    if (setBriefDirty && !force) {
      return;
    }
    if (force) {
      setBriefDirty = false;
    }
    $setBrief.val(
      JSON.stringify(
        composeSetBrief(state, { prompt: currentPrompt() }),
        null,
        2
      )
    );
  };

  const setActive = (selector, dataAttr, value) => {
    $root.find(selector).each(function () {
      const el = $(this);
      el.toggleClass('active', el.attr(dataAttr) === String(value));
    });
  };

  // Le nove dimensioni «Personalizzata» (spec
  // 2026-08-07-builder-custom-values-design.md) replicano il pattern
  // .ai-bgb-frame-custom: l'input si mostra solo a selezione 'custom' e non
  // si rivalorizza mentre l'utente ci sta scrivendo dentro, altrimenti ogni
  // tasto premuto sposterebbe il cursore all'inizio.
  const renderCustomField = (cssClass, value, isCustomSelected) => {
    const $field = $root.find(cssClass);
    $field.toggle(isCustomSelected);
    if (!$field.is(':focus')) {
      $field.val(value);
    }
  };

  const elementLabel = (element) =>
    element.itemKey === CUSTOM_ELEMENT_KEY
      ? element.customText
      : findElementItem(element.itemKey).label;

  const elementIcon = (element) =>
    elementIconSvg(element.itemKey, { size: 16 });

  const renderElementList = () => {
    const $list = $root.find('.ai-bgb-element-list');
    if (!state.elements.length) {
      $list.html('<div class="ai-bgb-hint">Nessun elemento aggiunto.</div>');
      return;
    }
    $list.html(
      state.elements
        .map((element) => {
          const zoneCombo = findZoneCombo(element.cells);
          return `<div class="ai-bgb-element-row" data-zone="${
            element.cells[0]
          }">
            <span>${elementIcon(element)} ${escapeHtml(
            elementLabel(element)
          )} · ${zoneCombo.short}</span>
            <button type="button" class="btn btn-sm btn-light ai-bgb-el-remove" data-zone="${
              element.cells[0]
            }">Rimuovi</button>
          </div>`;
        })
        .join('')
    );
  };

  const renderPicker = () => {
    const $picker = $root.find('.ai-bgb-picker');
    const $home = $root.find('.ai-bgb-elements-home');
    const selectionActive = Boolean(state.selection);
    if (!selectionActive && !editCell) {
      $picker.hide();
      $home.show();
      return;
    }
    const cells = selectionActive
      ? state.selection.cells
      : getElementByCell(state, editCell).cells;
    const combo = findZoneCombo(cells);
    const current = editCell ? getElementByCell(state, editCell) : null;
    $picker
      .find('.ai-bgb-picker-title')
      .text(
        `Elemento per: ${combo.short}` +
          (selectionActive
            ? ' (clicca le celle accanto per allargare la zona)'
            : '')
      );
    setActive(
      '.ai-bgb-picker [data-item]',
      'data-item',
      current && current.itemKey
    );
    $picker
      .find('.ai-bgb-custom-text')
      .val(
        current && current.itemKey === CUSTOM_ELEMENT_KEY
          ? current.customText
          : ''
      );
    const $levers = $picker.find('.ai-bgb-picker-levers');
    const levers = current ? elementFigureLevers(current.itemKey) : null;
    if (levers && current.options) {
      $levers
        .html(
          levers
            .map(
              (lever) => `
        <div class="ai-bgb-sublbl">${lever.label}</div>
        <div class="ai-bgb-chips">${lever.options
          .map(
            (option) =>
              `<div class="ai-bgb-chip${
                current.options[lever.key] === option.key ? ' active' : ''
              }" data-lever="${lever.key}" data-lever-value="${option.key}">${
                option.label
              }</div>`
          )
          .join('')}</div>`
            )
            .join('')
        )
        .show();
    } else {
      $levers.hide().empty();
    }
    $picker.find('.ai-bgb-picker-remove').toggle(Boolean(current));
    $home.hide();
    $picker.show();
  };

  // Le thumbnail il cui asset non carica (404 finché gli asset non esistono,
  // o errore di rete) spariscono: resta visibile solo il fallback tipografico
  // già presente dietro/accanto (label, box della cornice). Ribindata a ogni
  // render perché il blueprint (filigrana e banda) viene rigenerato ad ogni
  // chiamata, quindi le sue img sono sempre nodi nuovi.
  const bindThumbFallback = () => {
    // Filigrana e banda usano background-image (fallback naturale: resta la
    // tinta); solo le card usano <img> e hanno bisogno del flag broken.
    $root
      .find('img.ai-bgb-card-img')
      .off('error.aibgb')
      .on('error.aibgb', function () {
        $(this).addClass('broken');
      });
  };

  const PREVIEW_RUN_LABELS = {
    empty: 'Genera anteprima',
    fresh: '↻ Rigenera',
    stale: '↻ Aggiorna anteprima',
    loading: 'Generazione…',
  };

  const previewStateNow = () => {
    if (previewInFlight) {
      return 'loading';
    }
    if (lastPreviewPrompt === null) {
      return 'empty';
    }
    return currentPrompt() === lastPreviewPrompt ? 'fresh' : 'stale';
  };

  const updatePreviewState = () => {
    if (!isSuperUser || !$preview || !$preview.length) {
      return;
    }
    const stato = previewStateNow();
    $preview.find('.ai-bgb-preview-box').attr('data-preview-state', stato);
    const $btn = $preview.find('.ai-bgb-preview-run');
    $btn.prop('disabled', stato === 'loading');
    $btn.toggleClass('ai-bgb-preview-run-hot', stato === 'stale');
    $btn.text(PREVIEW_RUN_LABELS[stato]);
  };

  // requestAnimationFrame può mancare negli ambienti di test più vecchi: il
  // fallback a timeout mantiene comunque il rilascio della classe .entering.
  const nextFrame =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (fn) => setTimeout(fn, 16);

  const swapPreviewImage = (imageUrl) => {
    const $stage = $preview.find('.ai-bgb-preview-stage');
    // Igiene dei layer: eventuali fondali di crossfade precedenti se ne vanno.
    $stage.find('img:not(.current)').remove();
    const $vecchia = $stage.find('img.current').removeClass('current');
    const $nuova = $(
      `<img class="current entering" src="${escapeHtml(
        imageUrl
      )}" alt="Anteprima dello sfondo generato">`
    );
    $stage.append($nuova);
    // Doppio frame: la classe si toglie dopo che lo stato iniziale (opacity 0)
    // è stato dipinto, altrimenti la transizione non parte.
    nextFrame(() => nextFrame(() => $nuova.removeClass('entering')));
    if ($vecchia.length) {
      setTimeout(() => $vecchia.remove(), 400);
    }
  };

  const render = () => {
    $root.find('.ai-bgb-tab').each(function () {
      $(this).toggleClass('active', $(this).attr('data-tab') === activeTab);
    });
    $root.find('.ai-bgb-panel').each(function () {
      $(this).toggle($(this).attr('data-panel') === activeTab);
    });

    // Flusso a passi: Avanti/Indietro accompagnano fino all’ultima tab, dove
    // compaiono i dettagli finali e il pulsante Genera.
    const stepIndex = TABS.indexOf(activeTab);
    const isLastStep = stepIndex === TABS.length - 1;
    $root.find('.ai-bgb-back').toggle(stepIndex > 0);
    $root.find('.ai-bgb-next').toggle(!isLastStep);
    $root.find('.ai-bgb-generate').toggle(isLastStep);
    $root.find('.ai-bgb-extras').toggle(isLastStep);
    if (!isLastStep) {
      $root
        .find('.ai-bgb-next')
        .text(`Avanti: ${TAB_TITLES[TABS[stepIndex + 1]]} →`);
    }

    setActive('.ai-bgb-card[data-essence]', 'data-essence', state.base.type);
    $root.find('[data-sub="gradient"]').toggle(state.base.type === 'gradient');
    if (state.base.type === 'gradient') {
      const { from, to } = resolvedGradientEndpoints(state);
      $root
        .find('.ai-bgb-color-dot[data-color-endpoint="from"] .ai-bgb-sw')
        .css('background-color', from.tint);
      $root
        .find('.ai-bgb-color-dot[data-color-endpoint="to"] .ai-bgb-sw')
        .css('background-color', to.tint);
    }
    $root.find('[data-sub="scene"]').toggle(state.base.type === 'scene');
    $root.find('[data-sub="paper"]').toggle(state.base.type === 'paper');
    $root.find('[data-sub="material"]').toggle(state.base.type === 'material');
    // Il picker è renderizzato tre volte (essenza carta, sfumato, scena): il
    // selettore resta senza scoping così le copie restano allineate e cambiando
    // essenza non si vede mai una selezione stantia. La spunta qui sotto legge
    // lo stato grezzo `paperOn`, non `isPaperActive`: è equivalente solo perché
    // il checkbox vive esclusivamente sotto sfumato e scena, gli stessi due
    // tipi in cui `isPaperActive` onora `paperOn` — un terzo punto di
    // montaggio del checkbox romperebbe questa equivalenza.
    setActive('.ai-bgb-card[data-paper]', 'data-paper', state.base.paper);
    setActive('.ai-bgb-chip[data-grain]', 'data-grain', state.base.grain);
    $root.find('.ai-bgb-paper-on').prop('checked', Boolean(state.base.paperOn));
    $root.find('.ai-bgb-paper-pick').toggle(Boolean(state.base.paperOn));
    setActive(
      '.ai-bgb-card[data-material]',
      'data-material',
      state.base.material
    );
    setActive(
      '.ai-bgb-chip[data-material-intensity]',
      'data-material-intensity',
      state.base.materialIntensity
    );
    setActive(
      '.ai-bgb-chip[data-gradient-intensity]',
      'data-gradient-intensity',
      state.base.gradientIntensity || 'medium'
    );
    // La simmetrica non si applica al radiale (già simmetrico): mostrala solo
    // per le direzioni lineari.
    $root
      .find(
        '.ai-bgb-adv[data-sub="mirror"], .ai-bgb-adv-hint[data-sub="mirror"]'
      )
      .toggle(state.base.direction !== 'edges');
    $root.find('.ai-bgb-mirror').prop('checked', Boolean(state.base.mirror));
    setActive('.ai-bgb-card[data-scene]', 'data-scene', state.base.sceneKey);
    renderCustomField(
      '.ai-bgb-scene-custom',
      state.base.customScene,
      state.base.sceneKey === 'custom'
    );
    setActive(
      '.ai-bgb-card[data-sacred-figure]',
      'data-sacred-figure',
      state.base.sacredFigure
    );
    renderCustomField(
      '.ai-bgb-sacred-figure-custom',
      state.base.customSacredFigure,
      state.base.sacredFigure === 'custom'
    );
    setActive(
      '.ai-bgb-chip[data-sacred-framing]',
      'data-sacred-framing',
      state.base.sacredFraming
    );
    renderCustomField(
      '.ai-bgb-sacred-framing-custom',
      state.base.customSacredFraming,
      state.base.sacredFraming === 'custom'
    );
    setActive(
      '.ai-bgb-chip[data-sacred-perspective]',
      'data-sacred-perspective',
      state.base.sacredPerspective
    );
    renderCustomField(
      '.ai-bgb-sacred-perspective-custom',
      state.base.customSacredPerspective,
      state.base.sacredPerspective === 'custom'
    );
    setActive(
      '.ai-bgb-chip[data-sacred-mood]',
      'data-sacred-mood',
      state.base.sacredMood
    );
    renderCustomField(
      '.ai-bgb-sacred-mood-custom',
      state.base.customSacredMood,
      state.base.sacredMood === 'custom'
    );
    $root
      .find('[data-sub="sacred"]')
      .toggle(
        state.base.type === 'scene' && state.base.sceneKey === SACRED_SCENE_KEY
      );
    setActive(
      '.ai-bgb-card[data-technique]',
      'data-technique',
      state.style.technique
    );
    renderCustomField(
      '.ai-bgb-technique-custom',
      state.style.customTechnique,
      state.style.technique === 'custom'
    );
    setActive(
      '.ai-bgb-card[data-intensity]',
      'data-intensity',
      state.style.intensity
    );
    renderCustomField(
      '.ai-bgb-intensity-custom',
      state.style.customIntensity,
      state.style.intensity === 'custom'
    );
    setActive(
      '.ai-bgb-atmo[data-palette]',
      'data-palette',
      state.style.palette
    );
    renderCustomField(
      '.ai-bgb-palette-custom',
      state.style.customPalette,
      state.style.palette === 'custom'
    );
    setActive(
      '.ai-bgb-card[data-atmosphere]',
      'data-atmosphere',
      state.style.atmosphere
    );
    renderCustomField(
      '.ai-bgb-atmosphere-custom',
      state.style.customAtmosphere,
      state.style.atmosphere === 'custom'
    );
    $root
      .find('.ai-bgb-text-readability')
      .prop('checked', state.textReadability === true);
    const showArtisticControls = hasImagery(state);
    const showAtmosphereControls = supportsAtmosphere(state);
    $root.find('[data-sub="artistic"]').toggle(showArtisticControls);
    $root.find('[data-sub="atmosphere"]').toggle(showAtmosphereControls);
    $root
      .find('[data-sub="artistic-note"]')
      .toggle(!showArtisticControls && !showAtmosphereControls);
    $root
      .find('[data-sub="material-style-note"]')
      .toggle(!showArtisticControls && showAtmosphereControls);
    setActive('.ai-bgb-frame', 'data-frame-style', state.frame.style);
    setActive(
      '.ai-bgb-chip[data-frame-richness]',
      'data-frame-richness',
      state.frame.richness
    );
    setActive(
      '.ai-bgb-chip[data-frame-coverage]',
      'data-frame-coverage',
      state.frame.coverage
    );
    const hasFrameStyle = state.frame.style !== 'none';
    const hasGeometricFrame =
      hasFrameStyle && !isSoftFrameStyle(state.frame.style);
    $root.find('[data-sub="coverage"]').toggle(hasFrameStyle);
    $root.find('[data-sub="frame-tool"]').toggle(hasFrameStyle);
    $root.find('[data-sub="frame-relation"]').toggle(hasGeometricFrame);
    // Gli input di testo libero non si rivalorizzano mentre hanno il focus,
    // altrimenti ogni tasto premuto sposterebbe il cursore all’inizio.
    const $frameCustom = $root.find('.ai-bgb-frame-custom');
    $frameCustom.toggle(state.frame.style === 'custom');
    if (!$frameCustom.is(':focus')) {
      $frameCustom.val(state.frame.customStyle);
    }
    const $coverageCustom = $root.find('.ai-bgb-coverage-custom');
    $coverageCustom.toggle(state.frame.coverage === 'custom');
    if (!$coverageCustom.is(':focus')) {
      $coverageCustom.val(state.frame.customCoverage);
    }
    setActive(
      '.ai-bgb-chip[data-frame-relation]',
      'data-frame-relation',
      state.frame.relation
    );
    const marginSides = ['top', 'bottom', 'left', 'right'];
    const uniformMargin = marginSides.every(
      (side) => state.frame.margins[side] === state.frame.margins.top
    )
      ? state.frame.margins.top
      : null;
    setActive(
      '.ai-bgb-chip[data-frame-margin-all]',
      'data-frame-margin-all',
      uniformMargin
    );
    $root.find('.ai-bgb-chip[data-margin-side]').each(function () {
      const $chip = $(this);
      $chip.toggleClass(
        'active',
        $chip.attr('data-margin-level') ===
          state.frame.margins[$chip.attr('data-margin-side')]
      );
    });

    // Lo strumento visivo vive nel pannello attivo, nella modalità del
    // compito; gli altri contenitori restano vuoti.
    const toolMode =
      activeTab === 'elements'
        ? 'cells'
        : activeTab === 'frame'
        ? 'frame'
        : activeTab === 'base' && state.base.type === 'scene'
        ? 'scene'
        : activeTab === 'base' && state.base.type === 'gradient'
        ? 'gradient'
        : null;
    $root.find('.ai-bgb-tool-wrap').each(function () {
      const $wrap = $(this);
      const wanted =
        toolMode && $wrap.attr('data-tool') === toolMode
          ? renderBlueprintHtml(state, { ratio, mode: toolMode })
          : '';
      $wrap.html(wanted);
    });
    $root.find('.ai-bgb-zone').each(function () {
      const zoneKey = $(this).attr('data-zone');
      const allowed =
        Boolean(getElementByCell(state, zoneKey)) ||
        Boolean(state.selection) ||
        canAddElement(state);
      $(this).toggleClass('disabled', !allowed);
    });

    renderElementList();
    renderPicker();
    if (isSuperUser && $prompt && $prompt.length && !promptDirty) {
      $prompt.val(composeBuilderPrompt(state));
    }
    renderSetBrief();
    bindThumbFallback();
    updatePreviewState();
  };

  const setTab = (key) => {
    if (!TABS.includes(key)) {
      return;
    }
    activeTab = key;
    if (key !== 'elements') {
      editCell = null;
      state = clearSelection(state);
    }
    render();
  };

  $root.on('click', '.ai-bgb-tab', function () {
    setTab($(this).attr('data-tab'));
  });
  $root.on('click', '.ai-bgb-next', function () {
    const i = TABS.indexOf(activeTab);
    if (i < TABS.length - 1) {
      setTab(TABS[i + 1]);
    }
  });
  $root.on('click', '.ai-bgb-back', function () {
    const i = TABS.indexOf(activeTab);
    if (i > 0) {
      setTab(TABS[i - 1]);
    }
  });

  $root.on('click', '.ai-bgb-card[data-essence]', function () {
    state = setEssence(state, $(this).attr('data-essence'));
    render();
  });
  $root.on('click', '.ai-bgb-card[data-paper]', function () {
    state = setPaper(state, $(this).attr('data-paper'));
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-grain]', function () {
    state = setGrain(state, $(this).attr('data-grain'));
    render();
  });
  $root.on('change', '.ai-bgb-paper-on', function () {
    state = setPaperOn(state, this.checked);
    render();
  });
  $root.on('click', '.ai-bgb-card[data-material]', function () {
    state = setMaterial(state, $(this).attr('data-material'));
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-material-intensity]', function () {
    state = setMaterialIntensity(
      state,
      $(this).attr('data-material-intensity')
    );
    render();
  });
  $root.on('click', '.ai-bgb-grad-spot[data-direction]', function () {
    state = setDirection(state, $(this).attr('data-direction'));
    render();
  });
  $root.on(
    'mousedown',
    '.ai-bgb-grad-bar, .ai-bgb-grad-ring',
    function (event) {
      event.preventDefault();
      const sheet = $root.find('[data-tool="gradient"] .ai-bgb-sheet')[0];
      if (!sheet) {
        return;
      }
      const rect = sheet.getBoundingClientRect();
      const onMove = (moveEvent) => {
        const fraction = gradientFractionFromPointer({
          directionKey: state.base.direction,
          rect,
          clientX: moveEvent.clientX,
          clientY: moveEvent.clientY,
        });
        const spreadKey = snapSpreadKey(fraction, state.base.direction);
        if (spreadKey !== state.base.spread) {
          state = setSpread(state, spreadKey);
          render();
        }
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener(
        'mouseup',
        () => document.removeEventListener('mousemove', onMove),
        { once: true }
      );
    }
  );
  $root.on('click', '.ai-bgb-chip[data-gradient-intensity]', function () {
    state = setGradientIntensity(
      state,
      $(this).attr('data-gradient-intensity')
    );
    render();
  });
  $root.on('change', '.ai-bgb-mirror', function () {
    state = setMirror(state, $(this).is(':checked'));
    render();
  });
  $root.on('click', '.ai-bgb-color-dot', function () {
    const endpoint = $(this).attr('data-color-endpoint');
    const { from, to } = resolvedGradientEndpoints(state);
    openColorOverlay({
      host: $root[0],
      title: endpoint === 'from' ? 'Colore di partenza' : 'Colore di arrivo',
      tones: GRADIENT_TO_TONES,
      initial: endpoint === 'from' ? state.base.fromColor : state.base.toColor,
      resolvedTint: endpoint === 'from' ? from.tint : to.tint,
      onPick: (ref) => {
        state =
          endpoint === 'from'
            ? setFromColor(state, ref)
            : setToColor(state, ref);
        render();
      },
    });
  });
  $root.on('click', '.ai-bgb-card[data-scene]', function () {
    state = setScene(state, $(this).attr('data-scene'));
    render();
  });
  $root.on('input', '.ai-bgb-scene-custom', function () {
    state = setCustomScene(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-band-fade', function () {
    state = setSceneFade(state, $(this).attr('data-scene-fade') !== 'fade');
    render();
  });
  $root.on('mousedown', '.ai-bgb-band-handle', function (event) {
    event.preventDefault();
    const sheet = $root.find('[data-tool="scene"] .ai-bgb-sheet')[0];
    if (!sheet) {
      return;
    }
    const rect = sheet.getBoundingClientRect();
    const onMove = (moveEvent) => {
      const fraction = bandFractionFromPointer({
        placementKey: state.base.scenePlacement,
        rect,
        clientX: moveEvent.clientX,
        clientY: moveEvent.clientY,
      });
      const extentKey = snapExtentKey(fraction);
      if (extentKey !== state.base.sceneExtent) {
        state = setSceneExtent(state, extentKey);
        render();
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener(
      'mouseup',
      () => document.removeEventListener('mousemove', onMove),
      { once: true }
    );
  });
  $root.on('click', '.ai-bgb-card[data-sacred-figure]', function () {
    state = setSacredFigure(state, $(this).attr('data-sacred-figure'));
    render();
  });
  $root.on('input', '.ai-bgb-sacred-figure-custom', function () {
    state = setCustomSacredFigure(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-sacred-framing]', function () {
    state = setSacredFraming(state, $(this).attr('data-sacred-framing'));
    render();
  });
  $root.on('input', '.ai-bgb-sacred-framing-custom', function () {
    state = setCustomSacredFraming(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-sacred-perspective]', function () {
    state = setSacredPerspective(
      state,
      $(this).attr('data-sacred-perspective')
    );
    render();
  });
  $root.on('input', '.ai-bgb-sacred-perspective-custom', function () {
    state = setCustomSacredPerspective(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-sacred-mood]', function () {
    state = setSacredMood(state, $(this).attr('data-sacred-mood'));
    render();
  });
  $root.on('input', '.ai-bgb-sacred-mood-custom', function () {
    state = setCustomSacredMood(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-card[data-technique]', function () {
    state = setTechnique(state, $(this).attr('data-technique'));
    render();
  });
  $root.on('input', '.ai-bgb-technique-custom', function () {
    state = setCustomTechnique(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-card[data-intensity]', function () {
    state = setIntensity(state, $(this).attr('data-intensity'));
    render();
  });
  $root.on('input', '.ai-bgb-intensity-custom', function () {
    state = setCustomIntensity(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-atmo[data-palette]', function () {
    state = setPalette(state, $(this).attr('data-palette'));
    render();
  });
  $root.on('input', '.ai-bgb-palette-custom', function () {
    state = setCustomPalette(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-card[data-atmosphere]', function () {
    state = setAtmosphere(state, $(this).attr('data-atmosphere'));
    render();
  });
  $root.on('input', '.ai-bgb-atmosphere-custom', function () {
    state = setCustomAtmosphere(state, $(this).val());
    render();
  });
  $root.on('change', '.ai-bgb-text-readability', function () {
    state = setTextReadability(state, this.checked);
    render();
  });

  const rejectCell = (cell) => {
    const $cell = $root.find(`.ai-bgb-zone[data-zone="${cell}"]`);
    $cell.addClass('rejected');
    setTimeout(() => $cell.removeClass('rejected'), 300);
  };

  $root.on('click', '.ai-bgb-zone', function () {
    const cell = $(this).attr('data-zone');
    if (editCell) {
      const edited = getElementByCell(state, editCell);
      const next = toggleElementCell(state, editCell, cell);
      const rejected =
        next === state && getElementByCell(state, cell) !== edited;
      state = next;
      if (!getElementByCell(state, editCell)) {
        editCell =
          (edited && edited.cells.find((entry) => entry !== cell)) || null;
      }
      render();
      if (rejected) {
        rejectCell(cell);
      }
      return;
    }
    const action = cellClickAction(state, cell);
    if (action === 'reject') {
      rejectCell(cell);
      return;
    }
    let rejected = false;
    if (action === 'edit') {
      editCell = cell;
    } else if (action === 'start') {
      state = startCellSelection(state, cell);
    } else {
      const next = toggleSelectionCell(state, cell);
      rejected = next === state && !state.selection.cells.includes(cell);
      state = next;
    }
    render();
    if (rejected) {
      rejectCell(cell);
    }
  });
  $root.on('click', '.ai-bgb-place-area', function () {
    state = setScenePlacement(state, $(this).attr('data-scene-placement'));
    render();
  });
  $root.on('click', '.ai-bgb-picker [data-item]', function () {
    const itemKey = $(this).attr('data-item');
    if (editCell) {
      state = setElementItem(state, editCell, { itemKey });
      editCell = null;
    } else {
      state = confirmSelectionElement(state, { itemKey });
    }
    render();
  });
  $root.on('click', '.ai-bgb-custom-add', function () {
    const customText = $root.find('.ai-bgb-custom-text').val();
    const next = editCell
      ? setElementItem(state, editCell, {
          itemKey: CUSTOM_ELEMENT_KEY,
          customText,
        })
      : confirmSelectionElement(state, {
          itemKey: CUSTOM_ELEMENT_KEY,
          customText,
        });
    if (next === state) {
      return; // testo vuoto: resta nel picker
    }
    state = next;
    editCell = null;
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-lever]', function () {
    state = setElementOption(
      state,
      editCell,
      $(this).attr('data-lever'),
      $(this).attr('data-lever-value')
    );
    render();
  });
  $root.on('click', '.ai-bgb-picker-cancel', function () {
    editCell = null;
    state = clearSelection(state);
    render();
  });
  $root.on('click', '.ai-bgb-picker-remove', function () {
    state = removeElementByCell(state, editCell);
    editCell = null;
    render();
  });
  $root.on('click', '.ai-bgb-el-remove', function () {
    state = removeElementByCell(state, $(this).attr('data-zone'));
    render();
  });

  $root.on('click', '.ai-bgb-frame', function () {
    state = setFrameStyle(state, $(this).attr('data-frame-style'));
    render();
  });
  $root.on('input', '.ai-bgb-frame-custom', function () {
    state = setFrameCustomStyle(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-frame-richness]', function () {
    state = setFrameRichness(state, $(this).attr('data-frame-richness'));
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-frame-coverage]', function () {
    state = setFrameCoverage(state, $(this).attr('data-frame-coverage'));
    render();
  });
  $root.on('input', '.ai-bgb-coverage-custom', function () {
    state = setFrameCustomCoverage(state, $(this).val());
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-frame-relation]', function () {
    state = setFrameRelation(state, $(this).attr('data-frame-relation'));
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-frame-margin-all]', function () {
    state = setFrameMargins(state, $(this).attr('data-frame-margin-all'));
    render();
  });
  $root.on('click', '.ai-bgb-chip[data-margin-side]', function () {
    state = setFrameMargin(
      state,
      $(this).attr('data-margin-side'),
      $(this).attr('data-margin-level')
    );
    render();
  });
  $root.on('change', '.ai-bgb-margin-toggle', function () {
    $root.find('[data-sub="margin-sides"]').toggle($(this).prop('checked'));
  });
  $root.on('input', '.ai-bgb-details', function () {
    state = setDetails(state, $(this).val());
    render();
  });
  if (isSuperUser && $prompt && $prompt.length) {
    $root.on('input', '.ai-bgb-prompt', () => {
      promptDirty = true;
      renderSetBrief();
      updatePreviewState();
    });
  }
  if (isSuperUser && $setBrief && $setBrief.length) {
    $root.on('input', '.ai-bgb-setbrief', () => {
      setBriefDirty = true;
    });
    $root.on('click', '.ai-bgb-setbrief-reset', () => {
      renderSetBrief({ force: true });
    });
    $root.on('click', '.ai-bgb-setbrief-copy', function () {
      const $btn = $(this);
      const testo = $setBrief.val();
      const fatto = () => {
        $btn.text('Copiato');
        setTimeout(() => $btn.text('Copia'), 1500);
      };
      // navigator.clipboard richiede un contesto sicuro: in sua assenza si
      // ricade sulla selezione del textarea, che l'utente puo' copiare a mano.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(testo)
          .then(fatto, () => $setBrief.select());
      } else {
        $setBrief.select();
      }
    });
  }

  if (isSuperUser && $preview && $preview.length) {
    const runPreview = () => {
      // Il click ripetuto e' il gesto naturale ("questo non mi piace, rifallo"):
      // va servito, ma una generazione per volta, altrimenti due risposte in
      // corsa si sovrascrivono a caso.
      if (previewInFlight) {
        return;
      }
      previewInFlight = true;
      const promptUsato = currentPrompt();
      $preview.find('.ai-bgb-preview-status').text('');
      updatePreviewState();

      previewPending = generatePreview({
        prompt: promptUsato,
        orientation: state.orientation,
      })
        .then(({ imageUrl }) =>
          preloadPreviewImage(imageUrl).then(() => {
            swapPreviewImage(imageUrl);
            lastPreviewPrompt = promptUsato;
          })
        )
        .catch((err) => {
          $preview
            .find('.ai-bgb-preview-status')
            .text(
              `Non riuscita: ${err && err.message ? err.message : 'errore'}`
            );
        })
        .then(() => {
          previewInFlight = false;
          updatePreviewState();
        });
    };
    $root.on('click', '.ai-bgb-preview-run', runPreview);
    $root.on('click', '.ai-bgb-preview-badge', runPreview);
    $root.on('click', '.ai-bgb-preview-stage', () => {
      // Scorciatoia solo quando serve aggiornare: da fresca il click sarebbe
      // una rigenerazione accidentale.
      if (previewStateNow() === 'stale') {
        runPreview();
      }
    });
  }

  $root.on('click', '.ai-bgb-generate', function () {
    if (onConfirm) {
      onConfirm({ ...state, prompt: currentPrompt() });
    }
  });

  render();

  return {
    getState: () => ({ ...state }),
    setTab,
    // I test devono poter attendere l'anteprima: e' l'unica parte asincrona.
    previewPending: () => previewPending,
  };
}

let builderModalOpen = false;

/**
 * Apre il builder e risolve con { orientation, base, style, elements,
 * frame, textReadability, details, prompt } oppure null se annullato.
 * Crea e distrugge il modal a ogni apertura, stili inclusi.
 */
export default async function openBackgroundBuilderModal() {
  if (builderModalOpen || document.getElementById(MODAL_ID)) {
    return null;
  }
  builderModalOpen = true;

  let isSuperUser = false;
  try {
    isSuperUser = await isLoggedUserSuperUser();
  } catch (err) {
    isSuperUser = false;
  }

  const width = window.canvas ? Math.round(window.canvas.width) : 0;
  const height = window.canvas ? Math.round(window.canvas.height) : 0;
  const orientation = width >= height ? 'horizontal' : 'vertical';
  const ratio = width > 0 && height > 0 ? `${width} / ${height}` : undefined;

  return new Promise((resolve) => {
    const styleElement = document.createElement('style');
    styleElement.id = 'ai-bgb-modal-styles';
    styleElement.textContent = BUILDER_MODAL_STYLES;
    document.head.appendChild(styleElement);

    const wrapper = document.createElement('div');
    wrapper.innerHTML = buildBuilderModalHtml({
      isSuperUser,
      orientation,
      ratio,
    });
    document.body.appendChild(wrapper);

    const $modal = $(`#${MODAL_ID}`);
    let result = null;

    wireBuilderModal($modal, {
      isSuperUser,
      orientation,
      ratio,
      onConfirm: (payload) => {
        result = payload;
        $modal.modal('hide');
      },
    });

    $modal.one('hidden.bs.modal', () => {
      $modal.remove();
      wrapper.remove();
      styleElement.remove();
      builderModalOpen = false;
      resolve(result);
    });

    $modal.modal('show');
  });
}
